import React, { useState, useEffect } from 'react';
import { Mail, Phone, Building2, Calendar, Search, RefreshCw, Trash2, Package, LogOut } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import AdminLogin from './AdminLogin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_auth_token');
      const authTime = localStorage.getItem('admin_auth_time');
      
      if (token && authTime) {
        const timeDiff = Date.now() - parseInt(authTime);
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        // Session expires after 24 hours
        if (hoursDiff < 24) {
          setIsAuthenticated(true);
        } else {
          handleLogout();
        }
      }
    };
    
    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_auth_time');
    setIsAuthenticated(false);
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API}/enquiries`);
      const sortedEnquiries = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setEnquiries(sortedEnquiries);
      setFilteredEnquiries(sortedEnquiries);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      setError('Failed to load enquiries. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchEnquiries();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredEnquiries(enquiries);
    } else {
      const filtered = enquiries.filter(enquiry =>
        enquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        enquiry.phone.includes(searchTerm)
      );
      setFilteredEnquiries(filtered);
    }
  }, [searchTerm, enquiries]);

  const handleDelete = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/enquiry/${enquiryId}`);
      fetchEnquiries(); // Refresh the list
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      alert('Failed to delete enquiry. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header with Logout */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-slate-600">Manage your website enquiries</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="gap-2">
            <LogOut size={16} />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Enquiries</CardTitle>
              <Package className="h-4 w-4 text-slate-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{enquiries.length}</div>
              <p className="text-xs text-slate-600 mt-1">All time submissions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Today</CardTitle>
              <Calendar className="h-4 w-4 text-slate-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {enquiries.filter(e => {
                  const today = new Date().toDateString();
                  const enquiryDate = new Date(e.created_at).toDateString();
                  return today === enquiryDate;
                }).length}
              </div>
              <p className="text-xs text-slate-600 mt-1">Enquiries today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <Calendar className="h-4 w-4 text-slate-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {enquiries.filter(e => {
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return new Date(e.created_at) > weekAgo;
                }).length}
              </div>
              <p className="text-xs text-slate-600 mt-1">Last 7 days</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Actions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Enquiry List</CardTitle>
            <CardDescription>View and manage all customer enquiries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search by name, email, company, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={fetchEnquiries} variant="outline" className="gap-2">
                <RefreshCw size={16} />
                Refresh
              </Button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-slate-600">Loading enquiries...</p>
              </div>
            ) : filteredEnquiries.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600">
                  {searchTerm ? 'No enquiries found matching your search.' : 'No enquiries yet.'}
                </p>
              </div>
            ) : (
              /* Enquiries Table */
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEnquiries.map((enquiry) => (
                        <TableRow key={enquiry.id}>
                          <TableCell className="font-medium whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar size={14} className="text-slate-400" />
                              {formatDate(enquiry.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-slate-900">{enquiry.name}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 size={14} className="text-slate-400" />
                              <span className="text-sm">{enquiry.company}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <a
                                href={`mailto:${enquiry.email}`}
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                <Mail size={14} />
                                {enquiry.email}
                              </a>
                              <a
                                href={`tel:${enquiry.phone}`}
                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                <Phone size={14} />
                                {enquiry.phone}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{enquiry.quantity || 'Not specified'}</span>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {enquiry.message || 'No message'}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(enquiry.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Results Count */}
            {!loading && filteredEnquiries.length > 0 && (
              <div className="mt-4 text-sm text-slate-600">
                Showing {filteredEnquiries.length} of {enquiries.length} enquiries
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-green-900 mb-1">🔒 Protected Dashboard</h4>
                <p className="text-sm text-green-800">
                  This admin panel is password protected. Session expires after 24 hours of inactivity.
                </p>
                <p className="text-xs text-green-700 mt-2">
                  <strong>Login Credentials:</strong> Username: admin | Password: Sharad@2025!<br />
                  <em>🔒 Keep these credentials secure and private.</em>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
